import React, { useEffect, useRef, useState } from "react";
import { Move, X } from "lucide-react";

const clamp = (value) => Math.max(0, Math.min(100, Math.round(value)));

// Changes the viewport only: the owner's private original media is never modified or uploaded again.
export default function PortraitCropEditor({ src, position, onApply, onClose }) {
  const [x, setX] = useState(position.x ?? 50);
  const [y, setY] = useState(position.y ?? 50);
  const gesture = useRef(null);

  useEffect(() => {
    function escape(event) { if (event.key === "Escape") onClose(); }
    document.addEventListener("keydown", escape);
    return () => document.removeEventListener("keydown", escape);
  }, [onClose]);

  function pointerDown(event) {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    const frame = event.currentTarget;
    gesture.current = {
      id: event.pointerId, x: event.clientX, y: event.clientY,
      startX: x, startY: y,
      width: Math.max(1, frame.clientWidth),
      height: Math.max(1, frame.clientHeight),
    };
    frame.setPointerCapture(event.pointerId);
  }
  function pointerMove(event) {
    const start = gesture.current;
    if (!start || start.id !== event.pointerId) return;
    setX(clamp(start.startX - (event.clientX - start.x) * 100 / start.width));
    setY(clamp(start.startY - (event.clientY - start.y) * 100 / start.height));
  }
  function pointerEnd(event) {
    if (gesture.current?.id !== event.pointerId) return;
    gesture.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[#231911]/70 p-4" role="presentation">
      <section role="dialog" aria-modal="true" aria-labelledby="pm-portrait-crop-title"
        data-testid="pm-portrait-crop-dialog"
        className="max-h-[95vh] w-full max-w-[430px] overflow-y-auto rounded-[24px] bg-[#FFF9F1] p-5 text-[#3B2C22] shadow-2xl sm:p-7">
        <div className="flex items-center justify-between gap-3">
          <h2 id="pm-portrait-crop-title" className="font-serif text-2xl">Редактировать фото</h2>
          <button type="button" className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#826751]/25"
            onClick={onClose} aria-label="Закрыть редактор фотографии"><X size={18}/></button>
        </div>
        <p className="mt-2 text-sm leading-6 text-[#705D4D]">Передвинь фотографию в рамке, чтобы лицо было хорошо видно. Формат останется 5:6.</p>
        <div className="mx-auto mt-5 w-[min(100%,240px)]">
          <div className="relative aspect-[5/6] cursor-grab touch-none select-none overflow-hidden rounded-2xl border-2 border-[#826751]/30 bg-[#EDE2D4] active:cursor-grabbing"
            data-testid="pm-portrait-crop-frame"
            onPointerDown={pointerDown} onPointerMove={pointerMove} onPointerUp={pointerEnd} onPointerCancel={pointerEnd}
            style={{ touchAction: "none" }}>
            <img src={src} draggable={false} alt="Предпросмотр положения портрета"
              className="h-full w-full select-none object-cover"
              style={{ objectPosition: x + "% " + y + "%" }} />
            <div className="pointer-events-none absolute bottom-2 right-2 rounded-full bg-[#FFF9F1]/90 p-2"><Move size={17}/></div>
          </div>
        </div>
        <div className="mt-5 space-y-4">
          <label className="block text-xs text-[#705D4D]">Положение по горизонтали
            <input type="range" min="0" max="100" step="1" value={x} onChange={(e) => setX(Number(e.target.value))}
              className="mt-2 block w-full accent-[#6F533D]" aria-label="Сдвиг портрета по горизонтали"/>
          </label>
          <label className="block text-xs text-[#705D4D]">Положение по вертикали
            <input type="range" min="0" max="100" step="1" value={y} onChange={(e) => setY(Number(e.target.value))}
              className="mt-2 block w-full accent-[#6F533D]" aria-label="Сдвиг портрета по вертикали"/>
          </label>
        </div>
        <div className="mt-6 flex flex-wrap justify-end gap-2">
          <button type="button" onClick={() => { setX(50); setY(50); }}
            className="min-h-11 rounded-xl border border-[#826751]/30 px-4 text-sm">По центру</button>
          <button type="button" onClick={onClose}
            className="min-h-11 rounded-xl border border-[#826751]/30 px-4 text-sm">Отмена</button>
          <button type="button" onClick={() => onApply({ x, y })}
            className="min-h-11 rounded-xl bg-[#533C2E] px-5 text-sm text-white" data-testid="pm-portrait-crop-apply">
            Применить
          </button>
        </div>
      </section>
    </div>
  );
}
